import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { EmptyState } from '../components/EmptyState';
import { Icon, type IconName } from '../components/Icon';
import { ScannedAt } from '../components/ScannedAt';
import { useUrlNumber, useUrlState } from '../lib/useUrlState';
import { timeAgo } from '../lib/agentCommands';
import { formatDateTime } from '../lib/format';
import { RefreshButton } from '../components/RefreshButton';

/**
 * One Activity trail — phase 6 §4. The place you go when you do NOT yet know
 * which log to open: agent commands, access decisions, scheduler runs and Atlas
 * reconciles as one reverse-chronological thread.
 *
 * It does not replace the specialised views — every row links back to the one it
 * came from. It is a read: no table records "activity", the API unions the four
 * that already exist.
 *
 * Not here, on purpose: the VPN connection audit (root-only, and folding it in
 * would leak its gate) and secret values (never in this app).
 */

const ACTIVITY = gql`
  query Activity($limit: Int, $offset: Int, $source: String, $actorKind: String, $actor: String) {
    activity(limit: $limit, offset: $offset, source: $source, actorKind: $actorKind, actor: $actor) {
      id
      at
      source
      actor
      actorKind
      action
      resource
      link
      outcome
      detail
    }
  }
`;

interface Event {
  id: string;
  at: string;
  source: string;
  actor: string;
  actorKind: string;
  action: string;
  resource?: string;
  link?: string;
  outcome?: string;
  detail?: string;
}

const PAGE_SIZE = 50;

/** Source → the page it came from, so the label is the vocabulary of that page. */
const SOURCE: Record<string, { label: string; icon: IconName }> = {
  access: { label: 'Access task', icon: 'access' },
  agent: { label: 'Agent command', icon: 'run' },
  scheduler: { label: 'Scheduler', icon: 'clock' },
  atlas: { label: 'Atlas whitelist', icon: 'shield' },
};

/**
 * `unknown` is its own bucket and reads as a gap, not as a human. A `job_run`
 * triggered by hand records THAT a person did it and never WHO — putting that in
 * the same bucket as an attributed row is exactly the confusion an activity trail
 * is supposed to remove.
 */
const ACTOR_KIND: Record<string, string> = {
  human: 'Person',
  agent: 'Agent',
  schedule: 'Schedule',
  unknown: 'Unattributed',
};

/** Terminal-good green, failure red, everything mid-flight stays neutral. */
function outcomeClass(o?: string): string {
  if (!o) return '';
  if (o === 'succeeded' || o === 'success') return 'confirmed';
  if (o === 'failed' || o === 'error') return 'failed';
  return '';
}

export function ActivityPage() {
  const [source, setSource] = useUrlState('source');
  const [kind, setKind] = useUrlState('kind');
  const [actor, setActor] = useUrlState('actor');
  const [page, setPage] = useUrlNumber('page', 1);

  const { data, loading, error, refetch } = useQuery<{ activity: Event[] }>(ACTIVITY, {
    variables: {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      source: source || null,
      actorKind: kind || null,
      actor: actor.trim() || null,
    },
    fetchPolicy: 'cache-and-network',
  });

  const rows = data?.activity ?? [];
  const anyFilter = Boolean(source || kind || actor.trim());

  if (error)
    return (
      <div className="page">
        <p className="error">Couldn&apos;t load the activity trail — the API didn&apos;t respond.</p>
        <p className="muted">
          Nothing is lost: this page only reads the logs the other pages write. Try the specific
          one you need — <Link to="/cluster-access/commands">commands</Link>,{' '}
          <Link to="/scheduler">scheduler</Link>, <Link to="/tasks">tasks</Link>.
        </p>
      </div>
    );

  return (
    <div className="page page--wide">
      <p className="muted">
        Who did what, where, and how it turned out — agent commands, access decisions, scheduler
        runs and Atlas reconciles in one thread. Come here when you don&apos;t yet know which log
        to open; every row links to the one it came from.
      </p>
      {/* Not a scan and not a new record — say what it actually is, so nobody
          treats a gap here as "nothing happened". */}
      <ScannedAt
        curated
        lead="Read from the existing logs on every load — nothing is recorded here."
        note="The VPN connection audit is deliberately excluded: it is root-only."
      />

      <div className="filter-row">
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
        <input
          type="search"
          placeholder="Filter by actor — an email, or an agent id…"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
        />
        <select value={source} onChange={(e) => setSource(e.target.value)} title="filter by source">
          <option value="">All sources</option>
          {Object.entries(SOURCE).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} title="human, agent, or schedule">
          <option value="">Anyone</option>
          {Object.entries(ACTOR_KIND).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        {anyFilter && (
          <button
            className="mini-btn"
            onClick={() => {
              setSource('');
              setKind('');
              setActor('');
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {loading && !data ? (
        <div className="skeleton-row-group">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="clock"
          title={anyFilter ? 'Nothing matches these filters' : 'No activity recorded yet'}
          body={
            anyFilter
              ? 'Try a wider range — the actor filter matches part of an email or an agent id.'
              : 'Rows appear as scheduler jobs run, access tasks move, and operators issue agent commands.'
          }
        />
      ) : (
        <>
          <table className="table-dense">
            <thead>
              {/* No SortHeader: this is a reverse-chronological THREAD, and the
                  server pages it newest-first. Re-sorting the 50 rows in hand
                  would silently reorder a window, not the trail. */}
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Where</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const src = SOURCE[e.source] ?? { label: e.source, icon: 'inbox' as IconName };
                return (
                  <tr key={e.id}>
                    <td className="text-xs" title={formatDateTime(e.at)}>
                      {timeAgo(e.at)}
                    </td>
                    <td>
                      <div>{e.actor}</div>
                      <div className="muted text-2xs">{ACTOR_KIND[e.actorKind] ?? e.actorKind}</div>
                    </td>
                    <td>
                      <div>{e.action}</div>
                      {e.detail && <div className="muted text-2xs">{e.detail}</div>}
                    </td>
                    <td>
                      <span className="dot-cell">
                        <Icon name={src.icon} />
                        {e.link ? (
                          <Link to={e.link}>{e.resource || src.label}</Link>
                        ) : (
                          <span>{e.resource || src.label}</span>
                        )}
                      </span>
                      <div className="muted text-2xs">{src.label}</div>
                    </td>
                    <td>
                      {e.outcome ? (
                        <span className={`badge ${outcomeClass(e.outcome)}`}>{e.outcome}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* The union reports no total — counting it would mean scanning four
              tables in full for a number nobody acts on. Page until it runs dry. */}
          <div className="pager">
            <span className="muted">
              Showing {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + rows.length}
              {rows.length < PAGE_SIZE ? ' (end)' : ''}
            </span>
            <div className="pager-btns">
              <button className="mini-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                ‹ Prev
              </button>
              <span className="muted">Page {page}</span>
              <button
                className="mini-btn"
                disabled={rows.length < PAGE_SIZE}
                onClick={() => setPage(page + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
